/* eslint-disable no-empty */
import {
  createContext,
  useCallback,
  useContext,
  useInsertionEffect,
  useState,
} from 'react';

import { useRouter } from 'next/router';
import { useSearchParams } from 'next/navigation';

import { ConfirmTermsModal } from '~/modules/login/pages/ConfirmTermsModal';
import { CompleteNameModal } from '~/modules/login/pages/CompleteNameModal';

import { useToast } from './toast';
import { isValidFullName } from '../utils/validate-full-name';
import { getUserSubscriptionType } from '../utils/get-user-subscription-type';
import customAxios, { setAuthToken } from '../services/customAxios';
import { getUserData, setUserParam } from '../services/controllers/auth';
import { ILoggedUserParamsData } from '../interfaces/ILoggedUserParamsData';
import { ILoggedUserData } from '../interfaces/ILoggedUserData';
import { IFCWithChildren } from '../interfaces/IFCWithChildren';
import { ICustomLoggedUserData } from '../interfaces/ICustomLoggedUserData';
import { useCookies } from '../hooks/use-cookies';
import { useAppAuthData } from '../hooks/use-app-auth-data';
import { sidebar } from '../constants/sidebar';
import { cookies } from '../constants/cookies';
import { SplashLoader } from '../components/SplashLoader';

interface IAuthContextData {
  user: ICustomLoggedUserData;
  userParams: ILoggedUserParamsData;
  satoshiUsagePercentage: number;
  hiddenCoins: number[];
  hiddenStocks: number[];
  satoshiFreeCredits: number;
  canUseWallet: boolean;
  // Gate temporário: usuário na whitelist de beta testers da Carteira (ver WalletBetaTesters no backend)
  isWalletBetaTester: boolean;
  hasWeb3Consent: boolean;
  setHasWeb3Consent: (value: boolean) => void;
  handleSwitchHideCoin: (coinId: number) => void;
  handleSwitchHideStock: (stockId: number) => void;
  updateUserParam: (
    field: keyof ILoggedUserParamsData,
    value: string | number | boolean
  ) => void;
  loadUser: () => Promise<void>;
  assignTempUser: (partial: Partial<ILoggedUserData>) => void;
  logout: () => void;
}

// Telas privadas: usadas tanto para exigir token quanto para decidir se o modal
// obrigatório de nome completo pode aparecer (não aparece em /promotional).
const isPrivateRoute = (asPath: string): boolean =>
  asPath.includes('/favorites') ||
  sidebar.some((item) =>
    asPath.split('?')[0].replace('/', '').includes(item.key)
  );

const AuthContext = createContext({} as IAuthContextData);

const AuthProvider: IFCWithChildren = ({ children }) => {
  const { showToast } = useToast();
  const { getItem, removeItem } = useCookies();
  const [hiddenCoins, setHiddenCoins] = useState<number[]>([]);
  const [hiddenStocks, setHiddenStocks] = useState<number[]>([]);
  const [canShow, setCanShow] = useState<boolean>(false);
  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
  const [openCompleteNameModal, setOpenCompleteNameModal] =
    useState<boolean>(false);
  const [satoshiFreeCredits, setSatoshiFreeCredits] = useState<number>(0);
  const [canUseWallet, setCanUseWallet] = useState<boolean>(false);
  // Gate temporário: whitelist de beta testers da Carteira
  const [isWalletBetaTester, setIsWalletBetaTester] = useState<boolean>(false);
  const [hasWeb3Consent, setHasWeb3Consent] = useState<boolean>(false);
  const [satoshiUsagePercentage, setSatoshiUsagePercentage] =
    useState<number>(0);
  const router = useRouter();
  const { remove: appAuthDataRemove } = useAppAuthData();
  const searchParams = useSearchParams();
  const [user, setUser] = useState<ICustomLoggedUserData>(
    {} as ICustomLoggedUserData
  );

  const handleSwitchHideStock = useCallback(
    (stockId: number) => {
      setHiddenStocks((prev) => {
        const toSet = prev.includes(stockId)
          ? prev.filter((where) => Number(where) !== Number(stockId))
          : [...prev, stockId];

        const method = prev.includes(stockId) ? 'delete' : 'post';
        customAxios[method](`/HiddenStock/${stockId}`);
        return toSet;
      });
    },
    [setHiddenStocks]
  );

  const handleSwitchHideCoin = useCallback(
    (coinId: number) => {
      setHiddenCoins((prev) => {
        const toSet = prev.includes(coinId)
          ? prev.filter((where) => Number(where) !== Number(coinId))
          : [...prev, coinId];

        const method = prev.includes(coinId) ? 'delete' : 'post';
        customAxios[method](`/HiddenCoin/${coinId}`);
        return toSet;
      });
    },
    [setHiddenCoins]
  );

  const updateUserParam = useCallback(
    (field: keyof ILoggedUserParamsData, value: string | number | boolean) => {
      const current = user.params?.[field];
      try {
        setUserParam(field, value);
        setUser((prev) => ({
          ...prev,
          params: {
            ...(prev.params || {}),
            [field]: value,
          } as ILoggedUserParamsData,
        }));
      } catch {
        showToast({
          type: 'error',
          description: 'Houve um erro ao atualizar a informação',
        });

        setUser((prev) => ({
          ...prev,
          params: {
            ...(prev.params || {}),
            [field]: current,
          } as ILoggedUserParamsData,
        }));
      }
    },
    [user, showToast]
  );

  const assignTempUser = useCallback(
    (partial: Partial<ICustomLoggedUserData>) => {
      setUser((prev) => ({
        ...prev,
        ...partial,
      }));
    },
    []
  );

  const acceptTerms = useCallback(() => {
    setUser((prev) => ({
      ...prev,
      params: {
        ...(prev.params || {}),
        acceptedTerms: true,
      } as ILoggedUserParamsData,
    }));

    updateUserParam('acceptedTerms', true);
    setOpenTermsModal(false);
  }, [updateUserParam]);

  const handleNameSaved = useCallback((name: string) => {
    setUser((prev) => ({
      ...prev,
      name,
    }));
    setOpenCompleteNameModal(false);
  }, []);

  const logout = useCallback(() => {
    removeItem(cookies.AUTH_TOKEN);
    appAuthDataRemove();
    if (router.asPath !== '/') {
      const queryParams = Object.fromEntries(
        Array.from(searchParams.entries())
      );
      const { logout: _logout, ...cleanQuery } = queryParams as Record<
        string,
        any
      >;
      router.push({
        pathname: '/',
        query: cleanQuery,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appAuthDataRemove, router]);

  const loadUser = useCallback(async () => {
    try {
      const token = getItem(cookies.AUTH_TOKEN);
      const userData = await getUserData(token);

      setSatoshiFreeCredits(userData.aiCreditsCurrent);
      setSatoshiUsagePercentage(userData.aiCreditsUsagePercentage || 0);
      setCanUseWallet(Boolean(userData.canUseWallet));
      setIsWalletBetaTester(Boolean(userData.isWalletBetaTester));
      setHasWeb3Consent(Boolean(userData.hasLatestWeb3Consent));
      setHiddenCoins(
        userData.hiddenCoins && userData.hiddenCoins.length > 0
          ? (userData.hiddenCoins as any[]).map((coin) => coin.id)
          : []
      );
      setHiddenStocks(
        userData.hiddenStocks && userData.hiddenStocks.length > 0
          ? (userData.hiddenStocks as any[]).map((stock) => stock.id)
          : []
      );
      setUser({
        id: String(userData.id),
        email: userData.email,
        name: userData.nome,
        params: userData.userParams,
        subscription: getUserSubscriptionType(userData.userType),
        freeTrial: userData.plano?.status === 'Periodo de Testes',
      });

      const { acceptedTerms } = userData.userParams;
      if (!acceptedTerms) {
        setOpenTermsModal(true);
      }

      // Cadastros antigos podem ter nome incompleto. O backend informa em nomeValido;
      // se o campo não vier, validamos localmente com a mesma regra.
      const nameIsValid =
        typeof userData.nomeValido === 'boolean'
          ? userData.nomeValido
          : isValidFullName(userData.nome);

      setOpenCompleteNameModal(!nameIsValid && isPrivateRoute(router.asPath));
    } catch (error) {
      removeItem(cookies.AUTH_TOKEN);
      showToast({
        type: 'error',
        position: 'top-right',
        title: 'Erro de verificação',
        description:
          'Ocorreu um erro ao obter os seus dados, contate nosso suporte no e-mail contact@cryptocontrol.pro',
      });

      if (router.asPath !== '/') {
        const queryParams = Object.fromEntries(
          Array.from(searchParams.entries())
        );
        router.push({
          pathname: '/',
          query: queryParams,
        });
      }
    } finally {
      setTimeout(() => {
        setCanShow(true);
      }, 500);
    }
  }, [getItem, removeItem, showToast, router, searchParams]);

  const checkLoggedUserAtPublicRoute = useCallback(() => {
    const { query } = router;
    if (router.asPath.includes('/inscricoes-encerradas')) {
      setCanShow(true);
      return;
    }

    if (router.asPath.includes('/promotional')) {
      const accessToken = getItem(cookies.AUTH_TOKEN);
      if (accessToken) {
        setAuthToken(accessToken);
        loadUser();
      } else {
        setCanShow(true);
      }
      return;
    }

    if (query?.logout && Boolean(query.logout) === true) {
      setCanShow(true);
      logout();
      return;
    }

    const accessToken = getItem(cookies.AUTH_TOKEN);
    if (accessToken) {
      setAuthToken(accessToken);

      router.push({
        pathname: '/portfolio',
        query: window.location.search.replace('?', ''),
      });
    } else {
      setCanShow(true);
    }
  }, [router, getItem, logout]);

  useInsertionEffect(() => {
    const isAtPrivateScreen = isPrivateRoute(router.asPath);

    const hasAccessToken = !!getItem(cookies.AUTH_TOKEN);
    if (isAtPrivateScreen && !hasAccessToken) {
      setCanShow(false);
      logout();
      return;
    }
    if (isAtPrivateScreen && hasAccessToken) {
      setAuthToken(getItem(cookies.AUTH_TOKEN));
      loadUser();
    } else {
      checkLoggedUserAtPublicRoute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  return (
    <AuthContext.Provider
      value={{
        user,
        satoshiUsagePercentage,
        userParams: user.params || ({} as ILoggedUserParamsData),
        hiddenCoins,
        hiddenStocks,
        satoshiFreeCredits,
        canUseWallet,
        isWalletBetaTester,
        hasWeb3Consent,
        setHasWeb3Consent,
        handleSwitchHideCoin,
        handleSwitchHideStock,
        updateUserParam,
        loadUser,
        assignTempUser,
        logout,
      }}
    >
      <ConfirmTermsModal open={openTermsModal} close={acceptTerms} />
      <CompleteNameModal
        open={openCompleteNameModal && !openTermsModal}
        currentName={user.name}
        onSaved={handleNameSaved}
      />
      {canShow ? <>{children}</> : <SplashLoader />}
    </AuthContext.Provider>
  );
};

const useAuth = (): IAuthContextData => {
  return useContext(AuthContext);
};

export { AuthProvider, useAuth };
